import * as React from 'react';

import {
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    type BlockListItemData,
    type BlockOrderedListData,
    list,
} from '@gitbook/doc-core';
import { Block } from '@gitbook/slate';
import type { BlockProps, EditorContainer } from '@gitbook/slate-react';
import { Checkbox } from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { BlockWrapper, getBlockLineHeight } from '../common';
import { ListContext } from './ListContext';
import { useListItemBlockPaletteFactory } from './palette';

export function BlockListItem(props: BlockProps) {
    const { node, parent, children, editor } = props;

    const listContext = React.useContext(ListContext);
    assert(Block.isBlock(parent) && listContext, 'BlockListItem must be a child of a BlockList');

    const palette = useListItemBlockPaletteFactory({ nodeKey: node.key });

    return (
        <BlockWrapper tag="li" palette={palette} contentProps={{ className: 'flex' }} {...props}>
            {(() => {
                switch (listContext.type) {
                    case BLOCK_LIST_UL: {
                        return (
                            <ItemPrefix node={node}>
                                <PseudoBefore
                                    content={getUnorderedListItemsPrefixContent({
                                        depth: listContext.depth,
                                    })}
                                    fontFamily="Arial"
                                    style={{ fontSize: '1.5em', lineHeight: 1 }}
                                />
                            </ItemPrefix>
                        );
                    }
                    case BLOCK_LIST_OL: {
                        return (
                            <ItemPrefix node={node}>
                                <PseudoBefore
                                    content={getOrderedListItemPrefixContent({
                                        depth: listContext.depth,
                                        node,
                                        parent,
                                    })}
                                />
                            </ItemPrefix>
                        );
                    }
                    case BLOCK_LIST_TASKS:
                        return (
                            <ItemPrefix node={node}>
                                <TaskCheckbox node={node} editor={editor} />
                            </ItemPrefix>
                        );
                    default:
                        assertNever(listContext.type);
                }
            })()}
            <div className="flex flex-1 flex-col">{children}</div>
        </BlockWrapper>
    );
}

function getUnorderedListItemsPrefixContent(input: { depth: number }): string {
    switch (input.depth % 3) {
        case 0:
            return '•';
        case 1:
            return '◦';
        case 2:
            return '▪';
        default:
            return '•';
    }
}

function getOrderedListItemPrefixContent(input: {
    depth: number;
    node: Block;
    parent: Block;
}): string {
    const { node, parent } = input;
    const listData = parent.data.toFrozenObject<BlockOrderedListData>();
    const index = parent.getChildIndex(node.key);
    const start = listData.start || 1;
    const value = start + index;
    switch (input.depth % 3) {
        // Use numbers
        case 0: {
            return `${value}.`;
        }
        // Use letters
        case 1: {
            const letters = 'abcdefghijklmnopqrstuvwxyz';
            return `${letters[(value - 1) % letters.length]}.`;
        }
        // Use roman numbers
        case 2: {
            return `${toRoman(value).toLowerCase()}.`;
        }
        default:
            return '•';
    }
}

function toRoman(input: number): string {
    const lookup = {
        M: 1000,
        CM: 900,
        D: 500,
        CD: 400,
        C: 100,
        XC: 90,
        L: 50,
        XL: 40,
        X: 10,
        IX: 9,
        V: 5,
        IV: 4,
        I: 1,
    };
    let roman = '';
    let number = input;
    for (const i in lookup) {
        while (number >= lookup[i]) {
            roman += i;
            number -= lookup[i];
        }
    }
    return roman;
}

function TaskCheckbox(props: { node: Block; editor: EditorContainer }) {
    const { node, editor } = props;
    const { readOnly } = editor;
    const data = node.data.toFrozenObject<BlockListItemData>();
    return (
        <Checkbox
            value={!!data.checked}
            onChange={(value) => {
                editor.change((change) => {
                    list.checkTaskItem(change, node.key, value);
                });
            }}
            readOnly={readOnly}
            size="small"
        />
    );
}

function ItemPrefix(props: { node: Block; children: React.ReactNode }) {
    const firstBlock = props.node.nodes.first();
    assert(Block.isBlock(firstBlock), 'BlockListItem must have a block as first child');
    return (
        <div
            contentEditable={false}
            className="mr-1 flex items-center justify-center text-content-paragraph"
            style={{ height: getBlockLineHeight(firstBlock), minWidth: 24 }}
        >
            {props.children}
        </div>
    );
}

function PseudoBefore(props: {
    style?: React.CSSProperties;
    content: string;
    fontFamily?: string;
}) {
    return (
        <div
            className="before:font-var before:content-[--pseudoBefore--content]"
            style={
                {
                    '--pseudoBefore--content': `'${props.content}'`,
                    '--font-family': props.fontFamily ?? 'inherit',
                    ...props.style,
                } as React.CSSProperties
            }
        />
    );
}
